// src/store/slices/user/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { HttpClient } from '@/api/services/http';
import { AUTH_ENDPOINTS } from '@/api/services/http/config';
import { userService, User } from '@/api/services/userService.service';
// Define the user state interface
interface UserState {
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    workspace?: {
      id: string;
      name: string;
    };
  } | null;
  loading: boolean;
  error: string | null;
  allWorkspaceUsers: User[];
}

// Initial state
const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
  allWorkspaceUsers: [],
};

// Fetch user profile thunk
export const fetchUserProfile = createAsyncThunk(
  'user/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await HttpClient.apiClient.get(AUTH_ENDPOINTS.USER_PROFILE);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user profile');
    }
  }
);

export const fetchAllWorkspaceUsers = createAsyncThunk(
  'user/fetchAllWorkspaceUsers',
  async (_, { rejectWithValue }) => {
    console.log('Fetching all workspace users');
    try {
      const response = await userService.getAllWorkspaceUsers();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch all workspace users');
    }
  }
);

// User slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUser: (state) => {
      state.user = null;
    },
    setAllWorkspaceUsers: (state, action: PayloadAction<User[]>) => {
      state.allWorkspaceUsers = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAllWorkspaceUsers.fulfilled, (state, action) => {
        state.allWorkspaceUsers = action.payload;
      })
      .addCase(fetchAllWorkspaceUsers.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearUser } = userSlice.actions;

// Export the reducer directly
export const userReducer = userSlice.reducer;
